import { useState } from 'react';
import { toast } from 'sonner';
import { MoreHorizontal } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { InvoiceSummary } from '@/types/invoices';

type Action = 'send' | 'pay' | 'void';

/**
 * Only draft and sent invoices can be acted on, matching the API's guards. The
 * menu hides what the API would refuse rather than offering it and reporting a
 * 422, but the API remains the enforcement.
 *
 * Preview is deliberately not in here: it is a read, it has no status guard,
 * and it is offered to support partners too.
 */
function availableActions(status: string): Action[] {
  return status === 'draft' || status === 'sent' ? ['send', 'pay', 'void'] : [];
}

const LABELS: Record<Action, string> = {
  send: 'Email to admins',
  pay: 'Mark as paid',
  void: 'Void invoice',
};

const CONFIRMATIONS: Record<Action, { title: string; description: string; confirm: string }> = {
  send: {
    title: 'Email this invoice?',
    description:
      'The group admins receive the invoice PDF. Re-sending does not change the due date or the reminder schedule.',
    confirm: 'Send',
  },
  pay: {
    title: 'Mark this invoice as paid?',
    description:
      'Use this only for a payment taken outside the platform. The subscription is activated or resumed as if the payment had cleared, and no payment record is created.',
    confirm: 'Mark as paid',
  },
  void: {
    title: 'Void this invoice?',
    description:
      'This cannot be undone, and this billing period can never be invoiced again. If the group is suspended and this was its last unpaid invoice, the suspension is lifted.',
    confirm: 'Void invoice',
  },
};

export function InvoiceActions({
  invoice,
  canWrite,
  onDone,
}: {
  invoice: InvoiceSummary;
  canWrite: boolean;
  onDone: () => void;
}) {
  const [pending, setPending] = useState<Action | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const actions = canWrite ? availableActions(invoice.status) : [];

  /**
   * Fetched as a blob through the shared axios instance rather than opened as
   * a plain link: auth is cookie-based on the API origin, and a blob URL in an
   * iframe avoids both a popup blocker and a second round of credential
   * handling. The object URL is revoked when the dialog closes.
   */
  const openPreview = async () => {
    setIsPreviewing(true);
    try {
      const response = await api.get(`/invoices/${invoice.invoiceNumber}/preview`, { responseType: 'blob' });
      setPreviewUrl(URL.createObjectURL(response.data as Blob));
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setIsPreviewing(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const run = async () => {
    if (!pending) {
      return;
    }
    setIsRunning(true);
    try {
      await api.post(`/invoices/${invoice.invoiceNumber}/${pending}`);
      toast.success(`${LABELS[pending]} done for ${invoice.invoiceNumber}.`);
      setPending(null);
      onDone();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label={`Actions for ${invoice.invoiceNumber}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            disabled={isPreviewing}
            onSelect={(event) => {
              // Keep the menu's own close from racing the fetch.
              event.preventDefault();
              void openPreview();
            }}
          >
            {isPreviewing ? 'Opening preview...' : 'Preview invoice'}
          </DropdownMenuItem>
          {actions.map((action) => (
            <DropdownMenuItem key={action} onSelect={() => setPending(action)}>
              {LABELS[action]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={previewUrl !== null} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="h-[85vh] max-w-4xl">
          <DialogHeader>
            <DialogTitle>{invoice.invoiceNumber}</DialogTitle>
            <DialogDescription>
              The invoice exactly as the group can download it. Read only, nothing here changes it.
            </DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <iframe title={`Invoice ${invoice.invoiceNumber}`} src={previewUrl} className="h-full w-full rounded-md border" />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending ? CONFIRMATIONS[pending].title : ''}</AlertDialogTitle>
            <AlertDialogDescription>{pending ? CONFIRMATIONS[pending].description : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRunning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRunning}
              onClick={(event) => {
                // Keep the dialog mounted while the request is in flight, so the
                // button can show its pending state instead of the row vanishing
                // and reappearing.
                event.preventDefault();
                void run();
              }}
            >
              {isRunning ? 'Working...' : pending ? CONFIRMATIONS[pending].confirm : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
